import React, { useEffect } from 'react';
import { X, PencilLine, Globe, FileText, ArrowRight, KeyRound } from 'lucide-react';
import { AppIcon } from './Logo';
import type { Profile } from '../lib/api';
import type { ModoIa } from './QuizCreator';

interface Props {
  profile: Profile | null;
  onManual: () => void;
  /** Con qué fuente trabaja la IA: lo que investigue en la web o un PDF. */
  onConIa: (modo: ModoIa) => void;
  /** Lleva a la pestaña de IA del perfil, donde se guarda la clave. */
  onConfigurarIa: () => void;
  onClose: () => void;
}

/**
 * Pregunta cómo se quiere crear el cuestionario.
 *
 * Todas las vías llevan al mismo editor; lo único que cambia es si se abre con
 * el panel de generación desplegado, y en qué pestaña. Se pregunta antes porque son dos formas de
 * trabajar distintas —escribir las preguntas o revisarlas— y descubrir la
 * segunda escondida tras un botón dentro del editor dependía de la suerte.
 */
export const CreateModeModal: React.FC<Props> = ({
  profile,
  onManual,
  onConIa,
  onConfigurarIa,
  onClose,
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const tieneClaveIa = Boolean(profile?.aiKeyPreview);

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-950/85 backdrop-blur-sm flex items-start sm:items-center-safe justify-center p-4 overflow-y-auto"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="crear-titulo"
    >
      <div className="surface-raised bg-ink-900 w-full max-w-md my-auto animate-pop">
        <header className="flex items-start gap-3 px-6 pt-6 pb-5">
          <AppIcon size={32} />
          <div className="flex-1 min-w-0">
            <h2 id="crear-titulo" className="font-display font-semibold text-[1.0625rem] text-ink-50">
              Crear una trivia
            </h2>
            <p className="text-ink-400 text-[0.8125rem] leading-snug mt-0.5">
              ¿Cómo quieres empezar?
            </p>
          </div>
          <button
            onClick={onClose}
            className="-mt-1 -mr-2 p-2 rounded-lg text-ink-500 hover:text-ink-100 hover:bg-ink-800 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4.5 h-4.5" />
          </button>
        </header>

        <div className="px-6 pb-6 space-y-3">
          <Opcion
            icono={<PencilLine className="w-5 h-5" />}
            titulo="A mano"
            descripcion="Tú escribes cada pregunta y sus respuestas. Control total, sin depender de nada."
            onClick={onManual}
          />

          {tieneClaveIa ? (
            <>
              <Opcion
                icono={<Globe className="w-5 h-5" />}
                titulo="La IA investiga el tema"
                descripcion="Escribes solo el tema. La IA lo investiga en la web y te propone las preguntas con las fuentes que usó."
                onClick={() => onConIa('tema')}
                destacado
              />
              <Opcion
                icono={<FileText className="w-5 h-5" />}
                titulo="Desde tu PDF"
                descripcion="Subes un documento y la IA saca las preguntas solo de su contenido. Ideal para apuntes o temarios."
                onClick={() => onConIa('pdf')}
                destacado
              />
            </>
          ) : (
            // Sin clave no se ofrece la opción como si funcionara: se ofrece el
            // camino para activarla. Un botón que lleva a un error no es una
            // opción, es una trampa.
            <Opcion
              icono={<KeyRound className="w-5 h-5" />}
              titulo="Con IA"
              descripcion="Necesitas tu clave gratuita de Google AI Studio. Te explicamos cómo conseguirla en dos minutos."
              etiqueta="Configurar"
              onClick={onConfigurarIa}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const Opcion: React.FC<{
  icono: React.ReactNode;
  titulo: string;
  descripcion: string;
  etiqueta?: string;
  destacado?: boolean;
  onClick: () => void;
}> = ({ icono, titulo, descripcion, etiqueta, destacado, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-start gap-3.5 p-4 rounded-xl border text-left transition-colors ${
      destacado
        ? 'border-ember-500/40 bg-ember-500/5 hover:bg-ember-500/10'
        : 'border-ink-700 bg-ink-850 hover:bg-ink-800'
    }`}
  >
    <span className={`shrink-0 mt-0.5 ${destacado ? 'text-ember-400' : 'text-ink-400'}`}>
      {icono}
    </span>
    <span className="flex-1 min-w-0">
      <span className="block text-[0.9375rem] font-semibold text-ink-50">{titulo}</span>
      <span className="block text-[0.8125rem] leading-relaxed text-ink-400 mt-0.5">
        {descripcion}
      </span>
      {etiqueta && (
        <span className="inline-block mt-2 text-[0.8125rem] font-medium text-sky-400">
          {etiqueta} →
        </span>
      )}
    </span>
    {!etiqueta && <ArrowRight className="w-4 h-4 shrink-0 mt-1 text-ink-500" />}
  </button>
);
